import React, { useContext, useEffect, useMemo, useRef } from 'react';
import { useCookieConsent } from 'web/components/CookieConsentContext';
import useAnalytics from 'web/components/FirebaseContext/useAnalytics';
import usePerformance from 'web/components/FirebaseContext/usePerformance';
import useErrorReporter from 'web/hooks/useErrorReporter';
import HubspotContext, { HubspotProvider } from './HubspotContext';

type TrackingContextType = {
  trackPageView: (pathname: string) => void;
  trackEvent: (eventType: string, eventData?: Record<string, unknown>) => void;
  trackUser: (userId: string, userData?: { email?: string } & Record<string, unknown>) => void;
};

const TrackingContext = React.createContext<TrackingContextType>(null);

const disabledTracking: TrackingContextType = {
  trackPageView: () => undefined,
  trackEvent: () => undefined,
  trackUser: () => undefined,
};

const TrackingDisabledProvider = ({ children }: { children: React.ReactNode }) => (
  <TrackingContext.Provider value={disabledTracking}>{children}</TrackingContext.Provider>
);

const TrackingProvider = ({ children }: { children: React.ReactNode }) => {
  const { measurementAllowed } = useCookieConsent();
  const analytics = useAnalytics();
  usePerformance();
  const hubspot = useContext(HubspotContext);
  const errorReporter = useErrorReporter();
  const hubspotOnPathChanged = hubspot?.onPathChanged;
  const hubspotReset = hubspot?.reset;
  const hubspotSetUser = hubspot?.setUser;
  const pendingAnalyticsEvents = useRef<Array<{ eventType: string; eventData?: Record<string, unknown> }>>([]);
  const measurementAllowedRef = useRef(measurementAllowed);
  measurementAllowedRef.current = measurementAllowed;

  useEffect(() => {
    if (!measurementAllowed) {
      pendingAnalyticsEvents.current = [];
    } else if (analytics) {
      pendingAnalyticsEvents.current.splice(0).forEach(({ eventType, eventData }) => {
        try {
          analytics.logEvent(eventType, eventData);
        } catch (err) {
          errorReporter.report(new Error(`Failed to replay event: ${err}`));
        }
      });
    }
  }, [analytics, errorReporter, measurementAllowed]);

  const value = useMemo<TrackingContextType>(
    () => ({
      trackUser: (userId, userData) => {
        try {
          if (analytics) {
            analytics.setUserId(userId);
          }
          if (userId) {
            hubspotSetUser?.({
              email: userData.email,
              id: userId,
            });
          } else {
            hubspotReset?.();
          }
        } catch (err) {
          errorReporter.report(new Error(`Failed to track user: ${err}`));
        }
      },
      trackPageView: (pathname) => {
        try {
          hubspotOnPathChanged?.(pathname);
        } catch (err) {
          errorReporter.report(new Error(`Failed to track path change: ${err}`));
        }
      },
      trackEvent: (eventType, eventData) => {
        try {
          if (analytics) {
            analytics.logEvent(eventType, eventData);
          } else if (measurementAllowedRef.current) {
            pendingAnalyticsEvents.current = [...pendingAnalyticsEvents.current.slice(-19), { eventType, eventData }];
          }
        } catch (err) {
          errorReporter.report(new Error(`Failed to track event: ${err}`));
        }
      },
    }),
    [analytics, errorReporter, hubspotOnPathChanged, hubspotReset, hubspotSetUser],
  );
  return <TrackingContext.Provider value={value}>{children}</TrackingContext.Provider>;
};

const enabledProviders = [TrackingProvider, HubspotProvider];

const TrackingProviderWrapper = ({ children }: { children: React.ReactNode }) => (
  <>
    {enabledProviders.reduce(
      (acc, Provider) => (
        <Provider>{acc}</Provider>
      ),
      children,
    )}
  </>
);

export { TrackingDisabledProvider, TrackingProviderWrapper as TrackingProvider };
export default TrackingContext;
